<template>
  <Transition name="cookie">
    <section v-if="isVisible" class="cookie-banner" role="dialog" aria-label="Cookies notice">
      <button
        class="cookie-banner__close"
        aria-label="Close"
        @click="acceptEssential"
      >
        <Suspense>
          <base-icon name="base/Close" size="16px" />
        </Suspense>
      </button>

      <p class="cookie-banner__text">
        We use cookies for site functionality, analytics, personalization, and
        marketing.
      </p>

      <button
        type="button"
        class="cookie-banner__settings-link"
        @click="openSettings"
      >
        Change cookies settings
      </button>

      <div class="cookie-banner__actions">
        <base-button
          variant="dark_ghost"
          size="xs"
          font="b5"
          text="Reject All"
          no-shadow
          @click="rejectAll"
        />
        <base-button
          variant="dark"
          size="xs"
          font="b5"
          text="Accept All"
          no-shadow
          @click="acceptAll"
        />
      </div>
    </section>
  </Transition>
</template>

<script setup lang="ts">
import {
  getLocalStorage,
  writePersistentLocalStorage,
} from "~/services/LocalStorage/localStorage";
import { PopUpServices } from "~/services/PopUp/callPopUp";
import {
  COOKIE_CATEGORY_KEYS,
  COOKIE_DECISION_KEYS,
  type CookieConsent,
} from "~/services/Cookies/consent";

const route = useRoute();
const callPopUp = PopUpServices();
const isVisible = ref(false);

const EXCLUDED_PATHS = ["/cookies-policy", "/terms", "/privacy-policy"];

const persistConsent = (consent: CookieConsent, agreed: boolean) => {
  const now = new Date();
  COOKIE_CATEGORY_KEYS.forEach((key) => {
    writePersistentLocalStorage(`cookie_consent_${key}`, consent[key]);
  });
  if (agreed) {
    writePersistentLocalStorage(COOKIE_DECISION_KEYS.agreement, now);
  } else {
    writePersistentLocalStorage(COOKIE_DECISION_KEYS.essential, now);
  }
};

const acceptAll = () => {
  persistConsent(
    { analytics: true, ads: true, personalization: true, userData: true },
    true,
  );
  isVisible.value = false;
};

const rejectAll = () => {
  persistConsent(
    { analytics: false, ads: false, personalization: false, userData: false },
    false,
  );
  isVisible.value = false;
};

const acceptEssential = () => {
  rejectAll();
};

const openSettings = async () => {
  const result = await callPopUp.callPopUp({
    componentName: "base-cookie-settings-pop-up",
    persistent: false,
  });
  if (result && typeof result === "object") {
    isVisible.value = false;
  }
};

onMounted(() => {
  if (EXCLUDED_PATHS.includes(route.path)) return;
  const hasAgreement = getLocalStorage(COOKIE_DECISION_KEYS.agreement);
  const hasEssential = getLocalStorage(COOKIE_DECISION_KEYS.essential);
  if (!hasAgreement && !hasEssential) {
    isVisible.value = true;
  }
});
</script>

<style scoped lang="scss">
.cookie-banner {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 9000;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 320px;
  padding: 14px;
  padding-right: 36px;
  border-radius: 12px;
  background: var(--white-monochrome);
  box-shadow: 0 6px 24px 0 rgba(15, 23, 42, 0.12);

  &__close {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: var(--white-monochrome);
    color: var(--black-monochrome);
    cursor: pointer;
    outline: none;
  }

  &__text {
    margin: 0;
    color: var(--black-monochrome);
    font-family: "DM Sans", sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }

  &__settings-link {
    align-self: flex-start;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    color: var(--black-monochrome);
    font-family: inherit;
    font-size: 12px;
    line-height: 16px;
    text-decoration: underline;
    cursor: pointer;
    outline: none;
  }

  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 4px;

    :deep(.base-button) {
      flex: 1;
      min-height: 32px;
      height: 32px;
      padding: 6px 12px;
      p {
        font-size: 12px;
        line-height: 16px;
      }
    }
  }
}

@media (max-width: 768px) {
  .cookie-banner {
    left: 50%;
    right: auto;
    bottom: 16px;
    max-width: none;
    width: calc(100% - 32px);
    transform: translateX(-50%);
  }
}

.cookie-enter-active,
.cookie-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}
.cookie-enter-from,
.cookie-leave-to {
  opacity: 0;
  transform: translateY(16px);
}
@media (max-width: 768px) {
  .cookie-enter-from,
  .cookie-leave-to {
    transform: translate(-50%, 16px);
  }
  .cookie-enter-to,
  .cookie-leave-from {
    transform: translateX(-50%);
  }
}
</style>
